/**-----------------------------------------------------------------------------------------
* Copyright © 2020 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import { Directive, TemplateRef, Optional, Component, Input, ElementRef, ContentChild, NgModule } from '@angular/core';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';

import * as ɵngcc0 from '@angular/core';

const _c0 = ["*"];
class PDFTemplateDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
}
PDFTemplateDirective.ɵfac = function PDFTemplateDirective_Factory(t) { return new (t || PDFTemplateDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.TemplateRef, 8)); };
PDFTemplateDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: PDFTemplateDirective, selectors: [["", "kendoPDFTemplate", ""]] });
/** @nocollapse */
PDFTemplateDirective.ctorParameters = () => [
    { type: TemplateRef, decorators: [{ type: Optional }] }
];
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFTemplateDirective, [{
        type: Directive,
        args: [{
                selector: '[kendoPDFTemplate]'
            }]
    }], function () { return [{ type: ɵngcc0.TemplateRef, decorators: [{
                type: Optional
            }] }]; }, null); })();

const FIELDS = ['bottom', 'left', 'right', 'top'];
/**
 * Represents the Kendo UI PDFMargin component for Angular.
 */
class PDFMarginComponent {
    /**
     * @hidden
     */
    get options() {
        const options = {};
        for (let idx = 0; idx < FIELDS.length; idx++) {
            const field = FIELDS[idx];
            const value = this[field];
            if (typeof value !== 'undefined') {
                options[field] = value;
            }
        }
        return options;
    }
}
PDFMarginComponent.ɵfac = function PDFMarginComponent_Factory(t) { return new (t || PDFMarginComponent)(); };
PDFMarginComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFMarginComponent, selectors: [["kendo-pdf-export-margin"]], inputs: { left: "left", top: "top", right: "right", bottom: "bottom" }, decls: 0, vars: 0, template: function PDFMarginComponent_Template(rf, ctx) { }, encapsulation: 2 });
PDFMarginComponent.propDecorators = {
    left: [{ type: Input }],
    top: [{ type: Input }],
    right: [{ type: Input }],
    bottom: [{ type: Input }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFMarginComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export-margin',
                template: ``
            }]
    }], null, { left: [{
            type: Input
        }], top: [{
            type: Input
        }], right: [{
            type: Input
        }], bottom: [{
            type: Input
        }] }); })();

/**
 * @hidden
 */
const compileTemplate = (templateRef) => {
    const context = {};
    let embeddedView = templateRef.createEmbeddedView(context);
    const result = (data) => {
        Object.assign(context, data);
        embeddedView.detectChanges();
        const templateWrap = document.createElement('span');
        embeddedView.rootNodes.forEach((rootNode) => {
            templateWrap.appendChild(rootNode.cloneNode(true));
        });
        return templateWrap;
    };
    result.destroy = () => {
        embeddedView.destroy();
        embeddedView = null;
    };
    return result;
};

/**
 * Represents the [Kendo UI PDF Export component for Angular]({% slug overview_pdfexport %}).
 *
 * @example
 * ```ts-preview
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <div class="example-config">
 *       <button class="k-button" (click)="pdf.saveAs('document.pdf')">
 *         Save As PDF...
 *       </button>
 *     </div>
 *
 *     <kendo-pdf-export #pdf paperSize="A4" margin="2cm">
 *       Content goes here
 *     </kendo-pdf-export>
 *   `
 * })
 * export class AppComponent {
 * }
 * ```
 */
class PDFExportComponent {
    constructor(element) {
        this.element = element;
        /**
         * The creator of the PDF document.
         * @default "Kendo UI PDF Generator"
         */
        this.creator = 'Kendo UI PDF Generator';
        /**
         * Specifies the name of the exported PDF file.
         * @default "Export.pdf"
         */
        this.fileName = 'export.pdf';
    }
    get drawMargin() {
        const marginComponent = this.marginComponent;
        let margin = this.margin;
        if (marginComponent) {
            margin = Object.assign(margin || {}, marginComponent.options);
        }
        return margin;
    }
    /**
     * Saves the content as a PDF file with the specified name.
     * @param fileName - The name of the exported file.
     */
    saveAs(fileName = this.fileName) {
        this.save(this.element.nativeElement, fileName);
    }
    /**
     * Exports the content as a `Group` for further processing.
     *
     * @return - The root group of the exported scene.
     */
    export() {
        return this.exportElement(this.element.nativeElement);
    }
    save(element, fileName) {
        this.exportElement(element)
            .then(group => this.exportGroup(group, this.pdfOptions()))
            .then(dataUri => this.saveDataUri(dataUri, fileName, this.saveOptions()));
    }
    exportElement(element) {
        const promise = this.drawElement(element, this.drawOptions());
        const cleanup = this.cleanup.bind(this);
        promise.then(cleanup, cleanup);
        return promise;
    }
    cleanup() {
        if (this.pageTemplate) {
            this.pageTemplate.destroy();
            delete this.pageTemplate;
        }
    }
    drawOptions() {
        if (this.pageTemplateDirective) {
            this.pageTemplate = compileTemplate(this.pageTemplateDirective.templateRef);
        }
        return {
            avoidLinks: this.avoidLinks,
            forcePageBreak: this.forcePageBreak,
            keepTogether: this.keepTogether,
            margin: this.drawMargin,
            paperSize: this.paperSize,
            landscape: this.landscape,
            repeatHeaders: this.repeatHeaders,
            scale: this.scale,
            template: this.pageTemplate
        };
    }
    pdfOptions() {
        return {
            autoPrint: this.autoPrint,
            author: this.author,
            creator: this.creator,
            date: this.date,
            imgDPI: this.imageResolution,
            keywords: this.keywords,
            landscape: this.landscape,
            margin: this.drawMargin,
            multiPage: true,
            paperSize: this.paperSize,
            producer: this.producer,
            subject: this.subject,
            title: this.title
        };
    }
    saveOptions() {
        return {
            forceProxy: this.forceProxy,
            proxyData: this.proxyData,
            proxyTarget: this.proxyTarget,
            proxyURL: this.proxyURL
        };
    }
    drawElement(element, options) {
        return drawDOM(element, options);
    }
    exportGroup(group, options) {
        return exportPDF(group, options);
    }
    saveDataUri(dataUri, fileName, options) {
        saveAs(dataUri, fileName, options);
    }
}
PDFExportComponent.ɵfac = function PDFExportComponent_Factory(t) { return new (t || PDFExportComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
PDFExportComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFExportComponent, selectors: [["kendo-pdf-export"]], contentQueries: function PDFExportComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFTemplateDirective, true);
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFMarginComponent, true);
    } if (rf & 2) {
        let _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.pageTemplateDirective = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.marginComponent = _t.first);
    } }, inputs: { creator: "creator", fileName: "fileName", autoPrint: "autoPrint", author: "author", avoidLinks: "avoidLinks", forcePageBreak: "forcePageBreak", keepTogether: "keepTogether", date: "date", imageResolution: "imageResolution", forceProxy: "forceProxy", keywords: "keywords", landscape: "landscape", margin: "margin", paperSize: "paperSize", repeatHeaders: "repeatHeaders", scale: "scale", proxyData: "proxyData", proxyURL: "proxyURL", proxyTarget: "proxyTarget", producer: "producer", subject: "subject", title: "title" }, ngContentSelectors: _c0, decls: 2, vars: 0, template: function PDFExportComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵprojectionDef();
        ɵngcc0.ɵɵelementStart(0, "div");
        ɵngcc0.ɵɵprojection(1);
        ɵngcc0.ɵɵelementEnd();
    } }, encapsulation: 2 });
/** @nocollapse */
PDFExportComponent.ctorParameters = () => [
    { type: ElementRef }
];
PDFExportComponent.propDecorators = {
    autoPrint: [{ type: Input }],
    author: [{ type: Input }],
    avoidLinks: [{ type: Input }],
    forcePageBreak: [{ type: Input }],
    keepTogether: [{ type: Input }],
    creator: [{ type: Input }],
    date: [{ type: Input }],
    imageResolution: [{ type: Input }],
    fileName: [{ type: Input }],
    forceProxy: [{ type: Input }],
    keywords: [{ type: Input }],
    landscape: [{ type: Input }],
    margin: [{ type: Input }],
    paperSize: [{ type: Input }],
    repeatHeaders: [{ type: Input }],
    scale: [{ type: Input }],
    proxyData: [{ type: Input }],
    proxyURL: [{ type: Input }],
    proxyTarget: [{ type: Input }],
    producer: [{ type: Input }],
    subject: [{ type: Input }],
    title: [{ type: Input }],
    pageTemplateDirective: [{ type: ContentChild, args: [PDFTemplateDirective,] }],
    marginComponent: [{ type: ContentChild, args: [PDFMarginComponent,] }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export',
                template: `<div><ng-content></ng-content></div>`
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }]; }, { creator: [{
            type: Input
        }], fileName: [{
            type: Input
        }], autoPrint: [{
            type: Input
        }], author: [{
            type: Input
        }], avoidLinks: [{
            type: Input
        }], forcePageBreak: [{
            type: Input
        }], keepTogether: [{
            type: Input
        }], date: [{
            type: Input
        }], imageResolution: [{
            type: Input
        }], forceProxy: [{
            type: Input
        }], keywords: [{
            type: Input
        }], landscape: [{
            type: Input
        }], margin: [{
            type: Input
        }], paperSize: [{
            type: Input
        }], repeatHeaders: [{
            type: Input
        }], scale: [{
            type: Input
        }], proxyData: [{
            type: Input
        }], proxyURL: [{
            type: Input
        }], proxyTarget: [{
            type: Input
        }], producer: [{
            type: Input
        }], subject: [{
            type: Input
        }], title: [{
            type: Input
        }], pageTemplateDirective: [{
            type: ContentChild,
            args: [PDFTemplateDirective]
        }], marginComponent: [{
            type: ContentChild,
            args: [PDFMarginComponent]
        }] }); })();

const COMPONENT_DIRECTIVES = [
    PDFExportComponent,
    PDFMarginComponent,
    PDFTemplateDirective
];
/**
 * Represents the [NgModule]({{ site.data.urls.angular['ngmoduleapi'] }}) definition for the PDF Export directive.
 */
class PDFExportModule {
}
PDFExportModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: PDFExportModule });
PDFExportModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function PDFExportModule_Factory(t) { return new (t || PDFExportModule)(); } });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(PDFExportModule, { declarations: [PDFExportComponent, PDFMarginComponent, PDFTemplateDirective], exports: [PDFExportComponent, PDFMarginComponent, PDFTemplateDirective] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportModule, [{
        type: NgModule,
        args: [{
                declarations: [COMPONENT_DIRECTIVES],
                exports: [COMPONENT_DIRECTIVES]
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { PDFExportComponent, PDFExportModule, PDFMarginComponent, PDFTemplateDirective, compileTemplate };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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